import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft,
  RefreshCw,
  FileCode,
  Globe,
  Smartphone,
  Map,
  Star,
  Search,
  Bot,
  Code,
  History,
  CheckCircle2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Copy,
  Download,
  ExternalLink,
  Rss,
  FileText,
  Lightbulb
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import RelatedTools from "@/components/RelatedTools";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api/technical-seo`;

export default function TechnicalSEOTools() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTool, setActiveTool] = useState("robots-txt");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Get tool from URL params
  useEffect(() => {
    const toolFromUrl = searchParams.get('tool');
    if (toolFromUrl) {
      setActiveTool(toolFromUrl);
    }
  }, [searchParams]);

  // Form states
  const [url, setUrl] = useState("");
  const [userAgent, setUserAgent] = useState("Googlebot");
  const [testPath, setTestPath] = useState("/");
  const [htaccessRules, setHtaccessRules] = useState("");
  const [sitemapUrls, setSitemapUrls] = useState("");
  const [rssFeedUrl, setRssFeedUrl] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [schemaType, setSchemaType] = useState("Article");
  const [schemaData, setSchemaData] = useState({});
  const [businessName, setBusinessName] = useState("");
  const [placeId, setPlaceId] = useState("");
  const [localQuery, setLocalQuery] = useState("");
  const [localLocation, setLocalLocation] = useState("");

  const tools = [
    { id: "robots-txt", name: "robots.txt Tester", icon: <FileCode className="w-4 h-4" /> },
    { id: "htaccess", name: ".htaccess Tester", icon: <FileCode className="w-4 h-4" /> },
    { id: "sitemap", name: "Sitemap Generator", icon: <Map className="w-4 h-4" /> },
    { id: "rss-parser", name: "RSS Feed Parser", icon: <Rss className="w-4 h-4" /> },
    { id: "fetch-render", name: "Fetch & Render", icon: <Globe className="w-4 h-4" /> },
    { id: "mobile-friendly", name: "Mobile-Friendly Test", icon: <Smartphone className="w-4 h-4" /> },
    { id: "mobile-first", name: "Mobile-First Index", icon: <Smartphone className="w-4 h-4" /> },
    { id: "amp-validator", name: "AMP Validator", icon: <Code className="w-4 h-4" /> },
    { id: "hreflang", name: "Hreflang Tester", icon: <Globe className="w-4 h-4" /> },
    { id: "serp-simulator", name: "SERP Simulator", icon: <Search className="w-4 h-4" /> },
    { id: "schema", name: "Schema Generator", icon: <Code className="w-4 h-4" /> },
    { id: "algo-updates", name: "Algo Update Tracker", icon: <History className="w-4 h-4" /> },
    { id: "ai-bot", name: "AI Bot Access Tester", icon: <Bot className="w-4 h-4" /> },
    { id: "local-search", name: "Local Search Tool", icon: <Map className="w-4 h-4" /> },
    { id: "review-link", name: "Review Link Generator", icon: <Star className="w-4 h-4" /> },
  ];

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const downloadFile = (content, filename, type = "text/plain") => {
    const blob = new Blob([content], { type });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    toast.success(`Downloaded ${filename}`);
  };

  // API Handlers
  const handleRobotsTxtTest = async () => {
    if (!url) { toast.error("Please enter a URL"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/robots-txt/test`, {
        url, user_agent: userAgent, test_path: testPath
      });
      setResult(response.data);
      toast.success("robots.txt tested!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Test failed");
    } finally {
      setLoading(false);
    }
  };

  const handleHtaccessTest = async () => {
    if (!htaccessRules) { toast.error("Please enter .htaccess rules"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/htaccess/test`, {
        rules: htaccessRules, test_url: url || "https://example.com/page"
      });
      setResult(response.data);
      toast.success(".htaccess rules parsed!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Test failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSitemapGenerate = async () => {
    const urls = sitemapUrls.split('\n').filter(u => u.trim());
    if (urls.length === 0) { toast.error("Please enter URLs"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/sitemap/generate`, { urls });
      setResult(response.data);
      toast.success(`Sitemap generated with ${response.data.total_urls} URLs!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  // Helper to ensure URL has protocol
  const ensureProtocol = (inputUrl) => {
    if (!inputUrl) return inputUrl;
    const trimmed = inputUrl.trim();
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
      return 'https://' + trimmed;
    }
    return trimmed;
  };

  const handleRssParser = async () => {
    if (!rssFeedUrl) { toast.error("Please enter RSS feed URL"); return; }
    setLoading(true);
    try {
      const urlWithProtocol = ensureProtocol(rssFeedUrl);
      const response = await axios.post(`${API}/rss-feed/parse`, { url: urlWithProtocol });
      setResult(response.data);
      toast.success(`Parsed ${response.data.total_items} items!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Parse failed");
    } finally {
      setLoading(false);
    }
  };

  const handleFetchRender = async () => {
    if (!url) { toast.error("Please enter a URL"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/fetch-render`, { url });
      setResult(response.data);
      toast.success("Page fetched as different bots!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fetch failed");
    } finally {
      setLoading(false);
    }
  };

  const handleMobileFriendlyTest = async () => {
    if (!url) { toast.error("Please enter a URL"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/mobile-friendly/test`, { url });
      setResult(response.data);
      toast.success("Mobile-friendly test complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Test failed");
    } finally {
      setLoading(false);
    }
  };

  const handleMobileFirstCompare = async () => {
    if (!url) { toast.error("Please enter a URL"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/mobile-first-index/compare`, { url });
      setResult(response.data);
      toast.success("Mobile-First Index comparison complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Test failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAmpValidate = async () => {
    const urls = sitemapUrls.split('\n').filter(u => u.trim());
    if (urls.length === 0) { toast.error("Please enter AMP URLs"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/amp/validate`, { urls });
      setResult(response.data);
      toast.success(`Validated ${response.data.total_urls} URLs!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Validation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleHreflangTest = async () => {
    if (!url) { toast.error("Please enter a URL"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/hreflang/test`, { url, check_alternates: true });
      setResult(response.data);
      toast.success("Hreflang tags tested!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Test failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSerpSimulator = async () => {
    if (!title || !description) { toast.error("Please enter title and description"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/serp-simulator`, {
        title, description, url: url || "https://example.com"
      });
      setResult(response.data);
      toast.success("SERP preview generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Simulation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSchemaGenerate = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/schema/generate`, {
        schema_type: schemaType,
        data: schemaData
      });
      setResult(response.data);
      toast.success("Schema generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAlgoUpdates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/algo-updates`);
      setResult(response.data);
      toast.success("Algorithm updates loaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load updates");
    } finally {
      setLoading(false);
    }
  };

  const handleAiBotTest = async () => {
    if (!url) { toast.error("Please enter a URL"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/ai-bot-access/test`, { url });
      setResult(response.data);
      toast.success("AI bot access tested!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Test failed");
    } finally {
      setLoading(false);
    }
  };

  const handleLocalSearch = async () => {
    if (!localQuery || !localLocation) { toast.error("Please enter query and location"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/local-search/simulate`, {
        query: localQuery, location: localLocation, device: "desktop"
      });
      setResult(response.data);
      toast.success("Local search simulated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReviewLink = async () => {
    if (!businessName) { toast.error("Please enter business name"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/review-link/generate`, {
        business_name: businessName, place_id: placeId || null
      });
      setResult(response.data);
      toast.success("Review link generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const renderTool = () => {
    switch (activeTool) {
      case "robots-txt":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Website URL</label>
              <Input placeholder="example.com" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="robots-url-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">User Agent</label>
                <Select value={userAgent} onValueChange={setUserAgent}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Googlebot">Googlebot</SelectItem>
                    <SelectItem value="Bingbot">Bingbot</SelectItem>
                    <SelectItem value="*">All Bots (*)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Test Path</label>
                <Input placeholder="/" value={testPath} onChange={(e) => setTestPath(e.target.value)} />
              </div>
            </div>
            <Button onClick={handleRobotsTxtTest} disabled={loading} className="w-full" data-testid="test-robots-btn">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileCode className="w-4 h-4 mr-2" />}
              Test robots.txt
            </Button>
          </div>
        );

      case "htaccess":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">.htaccess Rules</label>
              <Textarea 
                placeholder="RewriteEngine On&#10;RewriteRule ^old-page$ /new-page [R=301,L]"
                rows={8}
                value={htaccessRules}
                onChange={(e) => setHtaccessRules(e.target.value)}
                data-testid="htaccess-input"
              />
            </div>
            <Button onClick={handleHtaccessTest} disabled={loading} className="w-full">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
              Test .htaccess Rules
            </Button>
          </div>
        );

      case "sitemap":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">URLs (one per line)</label>
              <Textarea 
                placeholder="https://example.com/&#10;https://example.com/about&#10;https://example.com/contact"
                rows={8}
                value={sitemapUrls}
                onChange={(e) => setSitemapUrls(e.target.value)}
                data-testid="sitemap-urls-input"
              />
            </div>
            <Button onClick={handleSitemapGenerate} disabled={loading} className="w-full bg-green-600 hover:bg-green-700">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Map className="w-4 h-4 mr-2" />}
              Generate Sitemap
            </Button>
          </div>
        );

      case "rss-parser":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">RSS Feed URL</label>
              <Input placeholder="https://example.com/feed.xml" value={rssFeedUrl} onChange={(e) => setRssFeedUrl(e.target.value)} data-testid="rss-url-input" />
            </div>
            <Button onClick={handleRssParser} disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Rss className="w-4 h-4 mr-2" />}
              Parse RSS Feed
            </Button>
          </div>
        );

      case "fetch-render":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">URL to Fetch</label>
              <Input placeholder="https://example.com" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="fetch-url-input" />
            </div>
            <p className="text-sm text-gray-500">Fetches page as Googlebot, Bingbot, Chrome, and Mobile to detect cloaking</p>
            <Button onClick={handleFetchRender} disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Globe className="w-4 h-4 mr-2" />}
              Fetch & Render
            </Button>
          </div>
        );

      case "mobile-friendly":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">URL to Test</label>
              <Input placeholder="https://example.com" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="mobile-url-input" />
            </div>
            <Button onClick={handleMobileFriendlyTest} disabled={loading} className="w-full bg-green-600 hover:bg-green-700">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Smartphone className="w-4 h-4 mr-2" />}
              Run Mobile-Friendly Test
            </Button>
          </div>
        );

      case "mobile-first":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">URL to Compare</label>
              <Input placeholder="https://example.com" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="mfi-url-input" />
            </div>
            <p className="text-sm text-gray-500">Compares mobile and desktop versions for Mobile-First Indexing readiness</p>
            <Button onClick={handleMobileFirstCompare} disabled={loading} className="w-full bg-purple-600 hover:bg-purple-700">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Smartphone className="w-4 h-4 mr-2" />}
              Compare Mobile vs Desktop
            </Button>
          </div>
        );

      case "amp-validator":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">AMP URLs (one per line)</label>
              <Textarea 
                placeholder="https://example.com/amp/page1&#10;https://example.com/amp/page2"
                rows={6}
                value={sitemapUrls}
                onChange={(e) => setSitemapUrls(e.target.value)}
                data-testid="amp-urls-input"
              />
            </div>
            <Button onClick={handleAmpValidate} disabled={loading} className="w-full bg-yellow-600 hover:bg-yellow-700">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Code className="w-4 h-4 mr-2" />}
              Validate AMP Pages
            </Button>
          </div>
        );

      case "hreflang":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">URL to Test</label>
              <Input placeholder="https://example.com" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="hreflang-url-input" />
            </div>
            <Button onClick={handleHreflangTest} disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Globe className="w-4 h-4 mr-2" />}
              Test Hreflang Tags
            </Button>
          </div>
        );

      case "serp-simulator":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title Tag</label>
              <Input placeholder="Your Page Title - Brand Name" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="serp-title-input" />
              <p className="text-xs text-gray-500 mt-1">{title.length}/60 characters</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Meta Description</label>
              <Textarea placeholder="Your compelling meta description that encourages clicks..." rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="serp-desc-input" />
              <p className="text-xs text-gray-500 mt-1">{description.length}/160 characters</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">URL</label>
              <Input placeholder="https://example.com/page" value={url} onChange={(e) => setUrl(e.target.value)} />
            </div>
            <Button onClick={handleSerpSimulator} disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Preview in SERP
            </Button>
          </div>
        );

      case "schema":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Schema Type</label>
              <Select value={schemaType} onValueChange={setSchemaType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Article">Article</SelectItem>
                  <SelectItem value="Product">Product</SelectItem>
                  <SelectItem value="LocalBusiness">Local Business</SelectItem>
                  <SelectItem value="FAQ">FAQ</SelectItem>
                  <SelectItem value="Organization">Organization</SelectItem>
                  <SelectItem value="BreadcrumbList">Breadcrumb</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Schema Data (JSON)</label>
              <Textarea 
                placeholder='{"headline": "Article Title", "author": "John Doe"}'
                rows={6}
                onChange={(e) => {
                  try { setSchemaData(JSON.parse(e.target.value)); } catch {}
                }}
                data-testid="schema-data-input"
              />
            </div>
            <Button onClick={handleSchemaGenerate} disabled={loading} className="w-full bg-purple-600 hover:bg-purple-700">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Code className="w-4 h-4 mr-2" />}
              Generate Schema
            </Button>
          </div>
        );

      case "algo-updates":
        return (
          <div className="space-y-4">
            <p className="text-gray-600">Get the latest Google algorithm updates and their impact on search rankings.</p>
            <Button onClick={handleAlgoUpdates} disabled={loading} className="w-full bg-red-600 hover:bg-red-700">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <History className="w-4 h-4 mr-2" />}
              Load Algorithm Updates
            </Button>
          </div>
        );

      case "ai-bot":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Website URL</label>
              <Input placeholder="example.com" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="aibot-url-input" />
            </div>
            <p className="text-sm text-gray-500">Check if AI bots (GPTBot, Google-Extended, Claude) can crawl your site</p>
            <Button onClick={handleAiBotTest} disabled={loading} className="w-full bg-violet-600 hover:bg-violet-700">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}
              Test AI Bot Access
            </Button>
          </div>
        );

      case "local-search":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Search Query</label>
              <Input placeholder="restaurants near me" value={localQuery} onChange={(e) => setLocalQuery(e.target.value)} data-testid="local-query-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Location</label>
              <Input placeholder="Mumbai, India" value={localLocation} onChange={(e) => setLocalLocation(e.target.value)} data-testid="local-location-input" />
            </div>
            <Button onClick={handleLocalSearch} disabled={loading} className="w-full bg-green-600 hover:bg-green-700">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Map className="w-4 h-4 mr-2" />}
              Simulate Local Search
            </Button>
          </div>
        );

      case "review-link":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Business Name</label>
              <Input placeholder="Your Business Name" value={businessName} onChange={(e) => setBusinessName(e.target.value)} data-testid="business-name-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Place ID (optional)</label>
              <Input placeholder="ChIJ..." value={placeId} onChange={(e) => setPlaceId(e.target.value)} />
              <p className="text-xs text-gray-500 mt-1">Find your Place ID at Google's Place ID Finder</p>
            </div>
            <Button onClick={handleReviewLink} disabled={loading} className="w-full bg-yellow-600 hover:bg-yellow-700">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Star className="w-4 h-4 mr-2" />}
              Generate Review Link
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  const renderResult = () => {
    if (!result) return null;

    // Common result rendering based on tool type
    if (activeTool === "robots-txt" && result) {
      return (
        <div className="mt-6 space-y-4">
          <div className={`p-4 rounded-lg ${result.test_result?.allowed ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center gap-2">
              {result.test_result?.allowed ? <CheckCircle2 className="w-6 h-6 text-green-600" /> : <XCircle className="w-6 h-6 text-red-600" />}
              <span className="font-semibold">{result.test_result?.allowed ? 'Path is ALLOWED' : 'Path is BLOCKED'}</span>
            </div>
            <p className="text-sm mt-1">{result.test_result?.reason}</p>
          </div>
          
          {result.sitemaps_found?.length > 0 && (
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="font-medium">Sitemaps Found:</p>
              {result.sitemaps_found.map((s, i) => (
                <a key={i} href={s} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline block">{s}</a>
              ))}
            </div>
          )}
          
          {result.content && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">robots.txt Content:</span>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(result.content)}><Copy className="w-4 h-4" /></Button>
              </div>
              <pre className="p-3 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto max-h-60">{result.content}</pre>
            </div>
          )}
        </div>
      );
    }

    if (activeTool === "sitemap" && result) {
      return (
        <div className="mt-6 space-y-4">
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="font-semibold text-green-800">Sitemap Generated: {result.total_urls} URLs</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => copyToClipboard(result.sitemap_xml)}><Copy className="w-4 h-4 mr-2" /> Copy XML</Button>
            <Button variant="outline" onClick={() => downloadFile(result.sitemap_xml, result.download_filename, 'application/xml')}><Download className="w-4 h-4 mr-2" /> Download</Button>
          </div>
          <pre className="p-3 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto max-h-60">{result.sitemap_xml}</pre>
        </div>
      );
    }

    if (activeTool === "mobile-friendly" && result) {
      return (
        <div className="mt-6 space-y-4">
          <div className={`p-4 rounded-lg ${result.mobile_friendly ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {result.mobile_friendly ? <CheckCircle2 className="w-6 h-6 text-green-600" /> : <XCircle className="w-6 h-6 text-red-600" />}
                <span className="font-semibold">{result.mobile_friendly ? 'Page is Mobile-Friendly!' : 'Page has Mobile Issues'}</span>
              </div>
              <span className="text-2xl font-bold">{result.score}/100</span>
            </div>
          </div>
          
          {result.issues?.length > 0 && (
            <div>
              <p className="font-medium mb-2">Issues Found:</p>
              {result.issues.map((issue, i) => (
                <div key={i} className={`p-3 mb-2 rounded-lg ${issue.severity === 'high' ? 'bg-red-50' : 'bg-yellow-50'}`}>
                  <p className="font-medium text-sm">{issue.issue}</p>
                  <p className="text-xs text-gray-600">{issue.fix}</p>
                </div>
              ))}
            </div>
          )}
          
          {result.passed_checks?.length > 0 && (
            <div>
              <p className="font-medium mb-2">Passed Checks:</p>
              {result.passed_checks.map((check, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-green-700">
                  <CheckCircle2 className="w-4 h-4" /> {check}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (activeTool === "serp-simulator" && result) {
      return (
        <div className="mt-6 space-y-4">
          <div className="p-4 bg-white border rounded-lg shadow-sm">
            <p className="text-xs text-gray-500 mb-1">{result.preview.url}</p>
            <h3 className="text-xl text-blue-700 hover:underline cursor-pointer">{result.preview.title}</h3>
            <p className="text-sm text-gray-600 mt-1">{result.preview.description}</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Title Score</p>
              <p className="text-2xl font-bold">{result.analysis.title.score}/100</p>
              <p className="text-xs">{result.analysis.title.length} chars ({result.analysis.title.optimal_range})</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Description Score</p>
              <p className="text-2xl font-bold">{result.analysis.description.score}/100</p>
              <p className="text-xs">{result.analysis.description.length} chars ({result.analysis.description.optimal_range})</p>
            </div>
          </div>
        </div>
      );
    }

    if (activeTool === "algo-updates" && result) {
      return (
        <div className="mt-6 space-y-4">
          {result.updates?.map((update, i) => (
            <div key={i} className="p-4 bg-gray-50 rounded-lg border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">{update.name}</h4>
                <Badge>{update.type}</Badge>
              </div>
              <p className="text-sm text-gray-600 mt-1">{update.description}</p>
              <p className="text-xs text-gray-500 mt-2">{update.date} • {update.status}</p>
            </div>
          ))}
        </div>
      );
    }

    if (activeTool === "ai-bot" && result) {
      return (
        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-green-600">{result.allowed_bots}</p>
              <p className="text-sm">Allowed</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-red-600">{result.blocked_bots}</p>
              <p className="text-sm">Blocked</p>
            </div>
          </div>
          
          {Object.entries(result.results || {}).map(([bot, data]) => (
            <div key={bot} className={`p-3 rounded-lg flex items-center justify-between ${data.allowed ? 'bg-green-50' : 'bg-red-50'}`}>
              <div>
                <p className="font-medium">{bot}</p>
                <p className="text-xs text-gray-600">{data.description}</p>
              </div>
              {data.allowed ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
            </div>
          ))}
        </div>
      );
    }

    if (activeTool === "schema" && result) {
      return (
        <div className="mt-6 space-y-4">
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => copyToClipboard(result.script_tag)}><Copy className="w-4 h-4 mr-2" /> Copy Script Tag</Button>
            <Button variant="outline" onClick={() => window.open(result.validation_url, '_blank')}><ExternalLink className="w-4 h-4 mr-2" /> Validate</Button>
          </div>
          <pre className="p-3 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto">{JSON.stringify(result.json_ld, null, 2)}</pre>
        </div>
      );
    }

    // RSS Feed Parser Results
    if (activeTool === "rss-parser" && result) {
      return (
        <div className="mt-6 space-y-4">
          <Card className="p-4 bg-green-50 border-green-200">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Parsed {result.total_items || 0} items from RSS Feed</span>
            </div>
            {result.feed_title && <p className="text-sm text-gray-600 mt-2">Feed: {result.feed_title}</p>}
          </Card>
          {result.items && result.items.length > 0 && (
            <div className="space-y-3">
              {result.items.slice(0, 10).map((item, idx) => (
                <Card key={idx} className="p-4 hover:shadow-md transition-shadow">
                  <h4 className="font-semibold text-gray-900">{item.title}</h4>
                  {item.published && <p className="text-xs text-gray-500 mt-1">{new Date(item.published).toLocaleDateString()}</p>}
                  {item.description && <p className="text-sm text-gray-600 mt-2 line-clamp-2">{item.description}</p>}
                  {item.link && (
                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-violet-600 text-sm mt-2 inline-flex items-center gap-1 hover:underline">
                      Read more <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Fetch & Render Results
    if (activeTool === "fetch-render" && result) {
      return (
        <div className="mt-6 space-y-4">
          <Card className="p-4 bg-blue-50 border-blue-200">
            <h4 className="font-semibold text-blue-700 mb-2">Page Fetched Successfully</h4>
            <p className="text-sm text-gray-600">URL: {result.url || url}</p>
          </Card>
          {result.googlebot && (
            <Card className="p-4">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Globe className="w-4 h-4" /> Googlebot View
              </h4>
              <div className="text-sm space-y-2">
                <p><span className="font-medium">Status:</span> {result.googlebot.status || 'OK'}</p>
                <p><span className="font-medium">Title:</span> {result.googlebot.title || 'N/A'}</p>
                <p><span className="font-medium">Content Length:</span> {result.googlebot.content_length || 'N/A'} bytes</p>
              </div>
            </Card>
          )}
          {result.mobile_bot && (
            <Card className="p-4">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Smartphone className="w-4 h-4" /> Mobile Bot View
              </h4>
              <div className="text-sm space-y-2">
                <p><span className="font-medium">Status:</span> {result.mobile_bot.status || 'OK'}</p>
                <p><span className="font-medium">Mobile Friendly:</span> {result.mobile_bot.mobile_friendly ? 'Yes' : 'No'}</p>
              </div>
            </Card>
          )}
        </div>
      );
    }

    // Mobile-First Index Results
    if (activeTool === "mobile-first" && result) {
      return (
        <div className="mt-6 space-y-4">
          <Card className={`p-4 ${result.is_mobile_friendly ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
            <div className="flex items-center gap-2">
              {result.is_mobile_friendly ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              )}
              <span className={`font-medium ${result.is_mobile_friendly ? 'text-green-700' : 'text-yellow-700'}`}>
                {result.is_mobile_friendly ? 'Mobile Friendly!' : 'Needs Mobile Optimization'}
              </span>
            </div>
          </Card>
          <div className="grid grid-cols-2 gap-4">
            <Card className="p-4">
              <h4 className="font-semibold text-gray-700 text-sm mb-2">Viewport</h4>
              <p className="text-lg font-bold">{result.viewport ? 'Configured' : 'Missing'}</p>
            </Card>
            <Card className="p-4">
              <h4 className="font-semibold text-gray-700 text-sm mb-2">Touch Elements</h4>
              <p className="text-lg font-bold">{result.touch_friendly ? 'Optimized' : 'Check Needed'}</p>
            </Card>
            <Card className="p-4">
              <h4 className="font-semibold text-gray-700 text-sm mb-2">Font Size</h4>
              <p className="text-lg font-bold">{result.font_size_ok ? 'Readable' : 'Too Small'}</p>
            </Card>
            <Card className="p-4">
              <h4 className="font-semibold text-gray-700 text-sm mb-2">Content Width</h4>
              <p className="text-lg font-bold">{result.content_fits_viewport ? 'Fits Screen' : 'Overflows'}</p>
            </Card>
          </div>
          {result.issues && result.issues.length > 0 && (
            <Card className="p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Issues Found</h4>
              <ul className="space-y-2">
                {result.issues.map((issue, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <span>{issue}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      );
    }

    // AMP Validator Results
    if (activeTool === "amp-validator" && result) {
      return (
        <div className="mt-6 space-y-4">
          <Card className={`p-4 ${result.is_valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-center gap-2">
              {result.is_valid ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
              <span className={`font-medium ${result.is_valid ? 'text-green-700' : 'text-red-700'}`}>
                {result.is_valid ? 'Valid AMP Page!' : 'AMP Validation Failed'}
              </span>
            </div>
          </Card>
          {result.errors && result.errors.length > 0 && (
            <Card className="p-4">
              <h4 className="font-semibold text-red-700 mb-2">Errors ({result.errors.length})</h4>
              <ul className="space-y-2">
                {result.errors.map((error, idx) => (
                  <li key={idx} className="text-sm text-red-600 bg-red-50 p-2 rounded">
                    {error.message || error}
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {result.warnings && result.warnings.length > 0 && (
            <Card className="p-4">
              <h4 className="font-semibold text-yellow-700 mb-2">Warnings ({result.warnings.length})</h4>
              <ul className="space-y-2">
                {result.warnings.map((warning, idx) => (
                  <li key={idx} className="text-sm text-yellow-600 bg-yellow-50 p-2 rounded">
                    {warning.message || warning}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      );
    }

    // Hreflang Tester Results
    if (activeTool === "hreflang" && result) {
      return (
        <div className="mt-6 space-y-4">
          <Card className={`p-4 ${result.is_valid ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
            <div className="flex items-center gap-2">
              {result.is_valid ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              )}
              <span className={`font-medium ${result.is_valid ? 'text-green-700' : 'text-yellow-700'}`}>
                {result.is_valid ? 'Hreflang Tags Valid!' : 'Hreflang Issues Found'}
              </span>
            </div>
          </Card>
          {result.tags && result.tags.length > 0 && (
            <Card className="p-4">
              <h4 className="font-semibold text-gray-900 mb-3">Hreflang Tags Found ({result.tags.length})</h4>
              <div className="space-y-2">
                {result.tags.map((tag, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                    <span className="font-mono">{tag.lang || tag.hreflang}</span>
                    <span className="text-gray-500 truncate max-w-xs">{tag.href}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {result.issues && result.issues.length > 0 && (
            <Card className="p-4">
              <h4 className="font-semibold text-yellow-700 mb-2">Issues</h4>
              <ul className="space-y-2">
                {result.issues.map((issue, idx) => (
                  <li key={idx} className="text-sm text-yellow-700 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {issue}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      );
    }

    // Local Search Results
    if (activeTool === "local-seo" && result) {
      return (
        <div className="mt-6 space-y-4">
          <Card className="p-4 bg-violet-50 border-violet-200">
            <h4 className="font-semibold text-violet-700 mb-2">Local SEO Analysis</h4>
            <p className="text-sm text-gray-600">Business: {result.business_name || 'N/A'}</p>
          </Card>
          <div className="grid grid-cols-2 gap-4">
            <Card className="p-4">
              <h4 className="font-semibold text-gray-700 text-sm mb-2">NAP Consistency</h4>
              <p className={`text-lg font-bold ${result.nap_consistent ? 'text-green-600' : 'text-red-600'}`}>
                {result.nap_consistent ? 'Consistent' : 'Inconsistent'}
              </p>
            </Card>
            <Card className="p-4">
              <h4 className="font-semibold text-gray-700 text-sm mb-2">Local Schema</h4>
              <p className={`text-lg font-bold ${result.has_local_schema ? 'text-green-600' : 'text-yellow-600'}`}>
                {result.has_local_schema ? 'Present' : 'Missing'}
              </p>
            </Card>
          </div>
          {result.recommendations && result.recommendations.length > 0 && (
            <Card className="p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Recommendations</h4>
              <ul className="space-y-2">
                {result.recommendations.map((rec, idx) => (
                  <li key={idx} className="text-sm flex items-start gap-2">
                    <Lightbulb className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                    {rec}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      );
    }

    // Generic JSON result display for other tools
    return (
      <div className="mt-6">
        <Card className="p-4">
          <h4 className="font-semibold text-gray-900 mb-3">Results</h4>
          <pre className="p-3 bg-gray-50 rounded-lg text-xs overflow-x-auto max-h-96">{JSON.stringify(result, null, 2)}</pre>
        </Card>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/tools/seo")} data-testid="back-button">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to SEO Tools
              </Button>
              <span className="text-xl font-bold bg-gradient-to-r from-violet-700 to-purple-600 bg-clip-text text-transparent">
                MarkeeMix
              </span>
            </div>
            <h1 className="text-lg font-bold text-gray-900">Technical SEO Tools</h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-4 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4">Technical Tools</h3>
              <div className="space-y-1 max-h-[70vh] overflow-y-auto">
                {tools.map((tool) => (
                  <button
                    key={tool.id}
                    onClick={() => { setActiveTool(tool.id); setResult(null); }}
                    data-testid={`tool-${tool.id}`}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-all text-sm ${
                      activeTool === tool.id
                        ? 'bg-violet-100 text-violet-700 font-medium'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {tool.icon}
                      <span>{tool.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                  {tools.find(t => t.id === activeTool)?.icon}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{tools.find(t => t.id === activeTool)?.name}</h2>
                </div>
              </div>

              {renderTool()}
              {renderResult()}
            </Card>
          </div>
        </div>

        {/* Related Tools Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <RelatedTools currentToolId={activeTool} category="seo" />
        </div>
      </div>
    </div>
  );
}
